import { fetchResponses } from "../../../Backend/backendService";

const cache = new Map();
let staleData = false;

function key(testId, questionId) {
  return `${testId}__${questionId}`;
}

function colsKey(testId, questionId) {
  return `cols__${testId}__${questionId}`;
}

export const CACHE_POLICY = {
  USE_CACHE: "use_cache",
  BYPASS: "bypass",
};

const SKIP_FIELDS = new Set([
  "id", "unique_id", "test_id", "question_id",
  "exp_time", "owner_id",
]);

const SKIP_COMMENT_FIELDS = new Set([
  "violations",   // add more here as needed
]);

function formatDate(val) {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function discoverCommentCols(rows) {
  for (const row of rows) {
    if (row.comments && typeof row.comments === "object") {
      return Object.keys(row.comments).filter(k => !SKIP_COMMENT_FIELDS.has(k));
    }
  }
  return [];
}

export function deriveColumns(rows) {
  if (!rows?.length) return [];

  const commentCols = discoverCommentCols(rows);
  const cols = [];
  const firstRow = rows[0];

  for (const field of Object.keys(firstRow)) {
    if (SKIP_FIELDS.has(field)) continue;

    if (field === "user_info") {
      cols.push("name", "className");
      continue;
    }

    if (field === "comments") {
      cols.push(...commentCols);
      continue;
    }

    cols.push(field);
  }

  return cols;
}

const BASE_COLS = new Set([
  "name", "className", "submission", "score",
  "quality", "status", "created_at", "rollid",
]);

export function buildMatrix(rows, columns) {
  const commentDerivedCols = columns.filter(c => !BASE_COLS.has(c));

  return rows.map(row => {
    return columns.map(col => {
      if (col === "name")        return row.user_info?.name      ?? null;
      if (col === "className")   return row.user_info?.className ?? null;
      if (col === "created_at")  return formatDate(row.created_at);

      if (commentDerivedCols.includes(col)) {
        const val = row.comments?.[col];
        if (val === null || val === undefined) return null;
        if (typeof val === "object") return JSON.stringify(val);
        return val;
      }

      const val = row[col];
      if (val === null || val === undefined) return null;
      if (typeof val === "object") return JSON.stringify(val);
      return val;
    });
  });
}

export async function getResponses(testId, questionId, policy = CACHE_POLICY.USE_CACHE) {
  const k  = key(testId, questionId);
  const ck = colsKey(testId, questionId);

  if (!staleData && policy === CACHE_POLICY.USE_CACHE && cache.has(k)) {
    return { rows: cache.get(k), columns: cache.get(ck), matrix: buildMatrix(cache.get(k), cache.get(ck)) };
  }

  const rawRows = await fetchResponses(testId, questionId);
  const columns = deriveColumns(rawRows);
  const matrix  = buildMatrix(rawRows, columns);

  cache.set(k,  rawRows);
  cache.set(ck, columns);

  staleData = false;
  return { rows: rawRows, columns, matrix };
}

export function invalidateCache(testId) {
  staleData = true;
  for (const k of cache.keys()) {
    if (k.startsWith(`${testId}__`) || k.startsWith(`cols__${testId}__`)) {
      cache.delete(k);
    }
  }
}